/* RESPONSIBLE TEAM: team-reporting */

import type {
  ChartConfig,
  ChartData,
  ChartDataResponse,
  DataRow,
  FractionUnit,
  GroupedData,
  MetricMetadata,
  MetricUnit,
  RenderContext,
} from '@/features/reporting-charts/types';
import { isDimensionCell, isDualQueryCell, isGroupedData } from '@/features/reporting-charts/types';
import {
  applyFractionUnit,
  findPeriod,
  formatDimensionDisplayValue,
  formatPeriodLabel,
  formatTimeDimensionLabel,
  formatCompactValue,
  formatValue,
  formatTimeTick,
  getDisplayNameWithAggregation,
  getMaxDataValueForMetric,
} from '@/features/reporting-charts/transforms/utils';
import {
  getDurationTickInterval,
  getValueTickInterval,
} from '@/features/reporting-charts/transforms/utils/axis';
import {
  MIN_POINT_LENGTH,
  SERIES_COLOR_PALETTE,
  COMPARISON_COLOR_PALETTE,
} from '@/features/reporting-charts/constants';
import { getSeriesColor } from '@/features/reporting-charts/transforms/color-registry';

/**
 * Highcharts series type - maps from our visualization types
 */
type HighchartsSeriesType = 'column' | 'bar' | 'line' | 'area';

/**
 * Internal structure for tracking series with their metric index for proper unit formatting.
 * We keep `metricIndex` and `previousTimestamps` internal only and strip them before returning
 * `Highcharts.Options`.
 */
interface SeriesWithMetricIndex {
  metricIndex: number;
  name: string;
  data: (number | null | [number, number | null] | { y: number | null; color: string })[];
  type: HighchartsSeriesType;
  yAxis?: number;
  color?: string;
  /**
   * Comparison series only. For each data point (indexed to match `data`), holds the
   * previous period's timestamp in ms. Used by the tooltip formatter to display the
   * previous period date rather than the current period x position.
   */
  previousTimestamps?: (number | null)[];
}

/**
 * Transforms ChartDataResponse into Highcharts options for series charts.
 *
 * Supported visualization types:
 * - 'bar' → Highcharts 'column' (vertical bars)
 * - 'horizontal-bar' → Highcharts 'bar' (horizontal bars)
 * - 'line' → Highcharts 'line'
 * - 'area' → Highcharts 'area'
 * - 'column-line' → Mixed column + line (first metric as column, rest as line)
 *
 * Data shapes supported:
 * - view_by only: [[dim, val, val], ...] - single dimension, multiple metrics
 * - view_by + segment_by: [[dim, dim, val, val], ...] - two dimensions, multiple metrics
 * - primitive categories: [["Jan", val], ...] - string/number as category
 */
export function buildSeriesOptions(
  data: ChartDataResponse,
  config: ChartConfig,
  renderContext: RenderContext,
): Highcharts.Options {
  const current = findPeriod(data, 'current');

  if (!current || !isGroupedData(current.data) || current.data.length === 0) {
    return emptyOptions(config, renderContext);
  }

  const primaryType = mapVisualizationType(config.visualizationType);
  const metricMetadata = current.metadata.metricMetadata;
  const totalColumns = current.metadata.columns.length;

  // Use metadata-based counting, but fall back to column inference when metricMetadata is empty
  // This handles legacy data where metricMetadata may not be populated
  const expectedDimensions = config.segmentBy ? 2 : 1;
  const numMetrics =
    metricMetadata.length > 0
      ? metricMetadata.length
      : Math.max(0, totalColumns - expectedDimensions);
  const numDimensions = Math.max(0, totalColumns - numMetrics);
  const isTimeSeries = config.viewBy === 'time';
  const isPivot = Boolean(config.segmentBy) && numDimensions >= 2;
  const hasDistinctUnits =
    numMetrics > 1 &&
    metricMetadata.length >= 2 &&
    metricMetadata[0]?.unit !== metricMetadata[1]?.unit;
  const shouldUseDualAxis =
    numMetrics > 1 &&
    !config.stacked &&
    (config.visualizationType === 'column-line' || hasDistinctUnits);

  // Build current period series based on data shape
  let series: SeriesWithMetricIndex[];
  let categories: string[] | undefined;

  // For area/line charts, series should be ordered by max Y value (smallest first)
  // This matches Ember's behavior and ensures smaller series aren't obscured in stacks
  const shouldOrderByMaxY =
    isPivot && (config.visualizationType === 'area' || config.visualizationType === 'line');

  if (isTimeSeries) {
    if (isPivot) {
      series = buildTimePivotSeries(current.data, metricMetadata, config, primaryType, numMetrics);
      if (shouldOrderByMaxY) {
        series = orderSeriesByMaxY(series);
      }
    } else {
      series = extractTimeSeries(
        current.data,
        metricMetadata,
        config,
        primaryType,
        numDimensions,
        numMetrics,
      );
    }
  } else if (isPivot) {
    const pivotResult = buildPivotSeries(
      current.data,
      metricMetadata,
      config,
      primaryType,
      numMetrics,
      renderContext,
    );
    series = pivotResult.series;
    if (shouldOrderByMaxY) {
      series = orderSeriesByMaxY(series);
    }
    categories = pivotResult.categories;
  } else {
    categories = extractCategories(current.data);
    series = extractSeries(
      current.data,
      metricMetadata,
      config,
      primaryType,
      numDimensions,
      numMetrics,
    );

    // Apply per-point colors for viewBy dimensions (fixed mappings or hash-based)
    if (config.viewBy) {
      const rawValues = current.data.map((row) => {
        const cell = row[0];
        return isDimensionCell(cell) ? String(cell.rawValue) : '';
      });
      for (const s of series) {
        s.data = s.data.map((value, i) => {
          const color = getSeriesColor(config.viewBy, rawValues[i]);
          if (!color) return value;
          const y = typeof value === 'number' ? value : null;
          return { y, color };
        });
      }
    }
  }

  // For stacked horizontal bars without a pivot, Highcharts needs each category as its own
  // series so they layer into a single composite bar. With 1 series and N data points,
  // stacking is invisible because each point gets its own bar.
  const isStackedSingleBar =
    config.visualizationType === 'horizontal-bar' &&
    config.stacked &&
    !isPivot &&
    series.length === 1 &&
    series[0].data.length > 1;

  if (isStackedSingleBar) {
    let categoryNames: string[];
    if (isTimeSeries) {
      categoryNames = current.data.map((row) => {
        const cell = row[0];
        const ts = extractTimestampMs(cell);
        if (ts !== null) {
          return formatTimeDimensionLabel(ts, config.viewByTimeInterval, renderContext);
        }
        return isDimensionCell(cell) ? formatDimensionDisplayValue(cell) : '';
      });
    } else {
      categoryNames = categories ?? [];
    }
    series = convertToStackedSingleBar(series[0], categoryNames, primaryType);
    categories = undefined;
  }

  // Add previous period series if time comparison enabled
  const previous = findPeriod(data, 'previous');
  const showTimeComparison =
    previous && config.visualizationOptions?.showTimeComparison && isGroupedData(previous.data);

  // For single-metric time comparison with dateRange, rename current period series to use date range label
  if (showTimeComparison && numMetrics === 1 && renderContext.dateRange && !isPivot) {
    const currentPeriodLabel = formatPeriodLabel(
      renderContext.dateRange,
      'current',
      renderContext.locale,
      renderContext.timezone,
    );
    if (currentPeriodLabel && series.length === 1) {
      series[0].name = currentPeriodLabel;
    }
  }

  if (showTimeComparison) {
    const comparisonSeries = buildComparisonSeries(
      previous,
      current.data,
      metricMetadata,
      config,
      primaryType,
      numDimensions,
      numMetrics,
      isTimeSeries,
      renderContext,
    );
    series.unshift(...comparisonSeries);
  }

  if (shouldUseDualAxis) {
    // For column-line charts, map first metric to left axis, second metric to right axis
    series = series.map((s) => ({ ...s, yAxis: s.metricIndex === 0 ? 0 : 1 }));
  }

  // Strip internal-only fields from series for final output
  const finalSeries: Highcharts.SeriesOptionsType[] = series.map(
    ({ metricIndex: _, previousTimestamps: __, ...rest }) => {
      return rest as unknown as Highcharts.SeriesOptionsType;
    },
  );

  const hasTarget = config.visualizationOptions?.target?.value !== undefined;

  // For stacked single bar, hide both axes entirely — just show the bar and legend (matches Ember)
  const yAxisConfig = isStackedSingleBar
    ? ({
        title: { text: undefined },
        labels: { enabled: false },
        gridLineWidth: 0,
        reversedStacks: false,
      } as Highcharts.YAxisOptions)
    : shouldUseDualAxis
      ? buildDualYAxis(metricMetadata, config, renderContext, series)
      : buildYAxis(metricMetadata, config, renderContext, getMaxDataValueForMetric(series, 0));

  const hideAxisLabels = config.compact === true;

  const options: Highcharts.Options = {
    time: {
      timezone: renderContext.timezone,
    },
    chart: {
      type: primaryType,
      // Add left margin when target line is configured to ensure label is visible
      ...(hasTarget && { marginLeft: 50 }),
      ...(hideAxisLabels && { spacingLeft: 0, spacingRight: 0 }),
      ...(renderContext.height && { height: renderContext.height }),
    },
    colors: SERIES_COLOR_PALETTE,
    xAxis: {
      type: isTimeSeries ? 'datetime' : 'category',
      ...(config.visualizationType === 'horizontal-bar' && isTimeSeries && { reversed: false }),
      ...(isTimeSeries
        ? {
            ...(config.viewByTimeInterval && {
              // Note: month uses a 30-day approximation, consistent with legacy behavior.
              tickInterval: getTimeTickIntervalInMs(config.viewByTimeInterval),
            }),
            labels: {
              formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                if (typeof this.value !== 'number') return '';
                return formatTimeTick(this.value, config.viewByTimeInterval, renderContext);
              },
            },
          }
        : { categories }),
      ...(isStackedSingleBar && { labels: { enabled: false }, tickLength: 0, lineWidth: 0 }),
      ...(hideAxisLabels && {
        labels: { enabled: false },
        lineWidth: 0,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
        startOnTick: false,
        endOnTick: false,
      }),
    },
    yAxis: hideAxisLabels
      ? {
          ...(yAxisConfig as Highcharts.YAxisOptions),
          labels: { enabled: false },
          gridLineWidth: 0,
        }
      : yAxisConfig,
    series: finalSeries,
    plotOptions: buildPlotOptions(config, primaryType, metricMetadata, renderContext),
    tooltip: hideAxisLabels
      ? buildCompactTooltip(config)
      : buildTooltip(series, metricMetadata, renderContext, config),
    legend: { enabled: true },
    credits: { enabled: false },
  };

  if (hideAxisLabels) {
    options.plotOptions = {
      ...options.plotOptions,
      column: {
        ...options.plotOptions?.column,
        maxPointWidth: undefined,
        pointPadding: 0.05,
        groupPadding: 0.1,
      },
    };
  }

  return options;
}

function getTimeTickIntervalInMs(interval: ChartConfig['viewByTimeInterval']): number | undefined {
  switch (interval) {
    case 'hour':
      return 60 * 60 * 1000;
    case 'day':
      return 24 * 60 * 60 * 1000;
    case 'week':
      return 7 * 24 * 60 * 60 * 1000;
    case 'month':
      return 30 * 24 * 60 * 60 * 1000;
    default:
      return undefined;
  }
}

function buildCompactTooltip(config: ChartConfig): Highcharts.TooltipOptions {
  const isMonthly = config.viewByTimeInterval === 'month';
  const metricName = config.chartSeries?.[0]?.metricDisplayName?.toLowerCase() ?? '';

  return {
    useHTML: true,
    pointFormat: `<div style="text-align:center">
        <div style="font-size:14px">{point.y} ${metricName}</div>
      </div>`,
    headerFormat: `<div style="text-align:center"><div style="font-size:12px;font-weight:600">{point.key}</div></div>`,
    xDateFormat: isMonthly ? '%b' : '%b %e',
  };
}

function buildTooltip(
  series: SeriesWithMetricIndex[],
  metricMetadata: MetricMetadata[],
  renderContext: RenderContext,
  config: ChartConfig,
): Highcharts.TooltipOptions {
  const seriesUnitMap = new Map<string, MetricUnit>();
  // Maps series name → per-point previous period timestamps (comparison series only).
  const comparisonTimestampsMap = new Map<string, (number | null)[]>();

  for (const s of series) {
    const unit = metricMetadata[s.metricIndex]?.unit ?? 'value';
    seriesUnitMap.set(s.name, unit);
    if (s.previousTimestamps) {
      comparisonTimestampsMap.set(s.name, s.previousTimestamps);
    }
  }

  const isTimeSeries = config.viewBy === 'time';
  const hasSegmentBy = Boolean(config.segmentBy);
  const isPercentStacking = config.stacked && config.visualizationOptions?.showRelativeValues;

  return {
    useHTML: true,
    outside: true,
    shared: false,
    backgroundColor: 'var(--neutral-fill)',
    borderColor: 'var(--neutral-border)',
    borderRadius: 10,
    style: {
      color: 'var(--text-on-neutral)',
    },
    formatter: function (this: Highcharts.Point) {
      const unit = seriesUnitMap.get(this.series.name) ?? 'value';
      const y = typeof this.y === 'number' ? this.y : null;
      const formattedValue = formatValue(y, unit, renderContext);

      let valueLine = formattedValue;
      if (isPercentStacking) {
        const percentage = (this as Highcharts.Point & { percentage?: number }).percentage;
        if (typeof percentage === 'number') {
          valueLine = `${Math.round(percentage * 100) / 100}% - ${formattedValue}`;
        }
      }

      let displayTimestamp: number | null = null;
      if (isTimeSeries && typeof this.x === 'number') {
        const previousTimestamps = comparisonTimestampsMap.get(this.series.name);
        const previousTs = previousTimestamps?.[this.index] ?? null;
        displayTimestamp = previousTs ?? this.x;
      }

      const datePart =
        displayTimestamp !== null
          ? formatTimeDimensionLabel(displayTimestamp, config.viewByTimeInterval, renderContext)
          : String(
              (this as Highcharts.Point & { key?: string }).key ||
                this.name ||
                this.series.name ||
                '',
            );

      if (hasSegmentBy) {
        const seriesColor = typeof this.series.color === 'string' ? this.series.color : '';
        const seriesName = this.series.name;
        return `<strong>${valueLine}</strong><br/><span style="color:${seriesColor}">\u25CF</span> ${seriesName}<br/>(${datePart})`;
      }

      return `<strong>${valueLine}</strong><br/>(${datePart})`;
    },
  };
}

function buildYAxis(
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  renderContext: RenderContext,
  maxDataValue?: number,
): Highcharts.YAxisOptions {
  const primaryUnit = metricMetadata[0]?.unit ?? 'value';
  const targetValue = config.visualizationOptions?.target?.value;
  const isPercentStacking = config.stacked && config.visualizationOptions?.showRelativeValues;

  const yAxis: Highcharts.YAxisOptions = {
    title: { text: undefined },
    allowDecimals: false,
    endOnTick: true,
    maxPadding: 0,
    softMax: targetValue ?? 2,
    // This ensures first series in array renders at bottom (larger values at bottom)
    ...(config.stacked && { reversedStacks: false }),
  };

  if (isPercentStacking) {
    yAxis.ceiling = 100;
    yAxis.tickInterval = 25;
    yAxis.labels = {
      format: '{text}%',
    };
  } else if (primaryUnit === 'percent' && !config.stacked) {
    yAxis.labels = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatValue(
          typeof this.value === 'number' ? this.value : null,
          primaryUnit,
          renderContext,
        );
      },
    };
  } else if (primaryUnit === 'percent' && config.stacked) {
    // Stacked percent metrics: auto-scale with 100% tick intervals (matches Embercom)
    yAxis.tickInterval = 100;
    yAxis.labels = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatValue(
          typeof this.value === 'number' ? this.value : null,
          primaryUnit,
          renderContext,
        );
      },
    };
  } else if (primaryUnit === 'value' && config.visualizationType === 'column-line') {
    const max = maxDataValue ?? 0;
    if (max === 0) {
      // No data: matches Ember's explicit tickInterval=5, which works with softMax: 2
      // to show a visible empty axis (ticks at 0 and 5).
      yAxis.tickInterval = 5;
    } else {
      const interval = getValueTickInterval(0, max);
      if (interval !== undefined) {
        yAxis.tickInterval = interval;
      }
      // else: out-of-table range → no tickInterval set → Highcharts auto-ticks,
      // exactly matching Ember's `return value || undefined` fallback behaviour.
    }
  } else if (primaryUnit === 'seconds' || primaryUnit === 'minutes' || primaryUnit === 'hours') {
    // Duration metrics: use smart tick intervals for "nice" axis labels (like Embercom)
    // tickPositioner has access to dataMin/dataMax at render time
    yAxis.tickPositioner = function () {
      // Access runtime axis properties (dataMax exists at runtime but not in types)
      const axis = this as Highcharts.Axis & { dataMax?: number };
      const max = axis.dataMax ?? 0;
      const interval = getDurationTickInterval(0, max);

      if (interval === undefined) {
        // Fall back to Highcharts default by returning linear ticks
        const defaultInterval = Math.ceil(max / 4) || 1;
        const ticks: number[] = [];
        for (let tick = 0; tick <= max; tick += defaultInterval) {
          ticks.push(tick);
        }
        return ticks;
      }

      // Generate tick positions from 0 to max (rounded up to nearest interval)
      const ticks: number[] = [];
      const maxTick = Math.ceil(max / interval) * interval;
      for (let tick = 0; tick <= maxTick; tick += interval) {
        ticks.push(tick);
      }
      return ticks;
    };
    yAxis.labels = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatValue(
          typeof this.value === 'number' ? this.value : null,
          primaryUnit,
          renderContext,
        );
      },
    };
  } else if (primaryUnit !== 'value') {
    yAxis.labels = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatValue(
          typeof this.value === 'number' ? this.value : null,
          primaryUnit,
          renderContext,
        );
      },
    };
  }

  // For value units, use compact notation for y-axis labels (e.g., 1K, 2.5M)
  // This covers both plain value charts and column-line (which only sets tickPositioner above)
  if (primaryUnit === 'value' && !yAxis.labels) {
    yAxis.labels = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatCompactValue(
          typeof this.value === 'number' ? this.value : null,
          primaryUnit,
          renderContext,
        );
      },
    };
  }

  if (targetValue !== undefined) {
    yAxis.plotLines = buildTargetLine(targetValue, primaryUnit, renderContext);
  }

  return yAxis;
}

function buildDualYAxis(
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  renderContext: RenderContext,
  series: SeriesWithMetricIndex[],
): Highcharts.YAxisOptions[] {
  const leftAxis = buildYAxis(
    metricMetadata.slice(0, 1),
    config,
    renderContext,
    getMaxDataValueForMetric(series, 0),
  );
  const rightAxis = buildYAxis(
    metricMetadata.slice(1, 2),
    config,
    renderContext,
    getMaxDataValueForMetric(series, 1),
  );

  rightAxis.opposite = true;
  // Target lines apply to the primary (left) axis only.
  rightAxis.plotLines = undefined;

  return [leftAxis, rightAxis];
}

function buildTargetLine(
  targetValue: number,
  unit: MetricUnit,
  renderContext: RenderContext,
): Highcharts.YAxisPlotLinesOptions[] {
  const targetValueFormatted = formatCompactValue(targetValue, unit, renderContext);

  return [
    {
      color: '#FF7F50',
      width: 2,
      value: targetValue,
      zIndex: 10,
      label: {
        align: 'left',
        verticalAlign: 'middle',
        x: -15,
        y: 2,
        useHTML: true,
        formatter() {
          return `<span class="flex text-on-accent font-medium rounded-lg bg-accent-fill whitespace-nowrap px-2 h-5 items-center text-center justify-center">${targetValueFormatted}</span>`;
        },
      },
    },
  ];
}

function mapVisualizationType(type: ChartConfig['visualizationType']): HighchartsSeriesType {
  switch (type) {
    case 'horizontal-bar':
      return 'bar';
    case 'line':
      return 'line';
    case 'area':
      return 'area';
    case 'column-line':
      return 'column';
    case 'bar':
    default:
      return 'column';
  }
}

function getSeriesTypeForIndex(
  index: number,
  totalMetrics: number,
  visualizationType: ChartConfig['visualizationType'],
  primaryType: HighchartsSeriesType,
): HighchartsSeriesType {
  if (visualizationType === 'column-line' && index > 0 && totalMetrics > 1) {
    return 'line';
  }
  return primaryType;
}

/**
 * Orders series by their maximum Y value in ascending order.
 * This matches Ember's HighchartsDataBuilder.orderSeries() behavior for line/area charts.
 * Placing smaller series at the bottom of stacked charts prevents them from being obscured.
 */
function orderSeriesByMaxY(series: SeriesWithMetricIndex[]): SeriesWithMetricIndex[] {
  return [...series].sort((a, b) => {
    const maxA = Math.max(
      ...a.data.map((point) => {
        if (Array.isArray(point)) {
          return point[1] ?? 0;
        }
        if (typeof point === 'object' && point !== null) {
          return point.y ?? 0;
        }
        return point ?? 0;
      }),
    );
    const maxB = Math.max(
      ...b.data.map((point) => {
        if (Array.isArray(point)) {
          return point[1] ?? 0;
        }
        if (typeof point === 'object' && point !== null) {
          return point.y ?? 0;
        }
        return point ?? 0;
      }),
    );
    return maxA - maxB;
  });
}

function convertToStackedSingleBar(
  singleSeries: SeriesWithMetricIndex,
  categoryNames: string[],
  seriesType: HighchartsSeriesType,
): SeriesWithMetricIndex[] {
  return singleSeries.data.map((point, i) => {
    let y: number | null;
    let color: string | undefined;

    if (point === null) {
      y = null;
    } else if (typeof point === 'number') {
      y = point;
    } else if (Array.isArray(point)) {
      y = point[1];
    } else {
      y = point.y;
      color = point.color;
    }

    return {
      name: categoryNames[i] ?? `Category ${i + 1}`,
      data: [y],
      type: seriesType,
      metricIndex: singleSeries.metricIndex,
      ...(color && { color }),
    };
  });
}

function buildPlotOptions(
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  metricMetadata: MetricMetadata[],
  renderContext: RenderContext,
): Highcharts.PlotOptions {
  const options: Highcharts.PlotOptions = {};
  const isPercentStacking = config.stacked && config.visualizationOptions?.showRelativeValues;
  const primaryUnit = metricMetadata[0]?.unit ?? 'value';

  // Limit bar width to 32px (matches Ember)
  if (primaryType === 'column') {
    options.column = { maxPointWidth: 32, minPointLength: MIN_POINT_LENGTH };
  }
  if (primaryType === 'bar') {
    options.bar = { maxPointWidth: 32, minPointLength: MIN_POINT_LENGTH };
  }

  if (config.stacked) {
    const stackingType = isPercentStacking ? 'percent' : 'normal';

    switch (primaryType) {
      case 'column':
        options.column = { ...options.column, stacking: stackingType, borderWidth: 0 };
        break;
      case 'bar':
        options.bar = { ...options.bar, stacking: stackingType, borderWidth: 0 };
        break;
      case 'area':
        options.area = { stacking: stackingType, fillOpacity: 0.1 };
        break;
    }
  }

  // Area charts get low fill opacity to look more like lines (matches Ember)
  if (config.visualizationType === 'area' && !config.stacked) {
    options.area = { ...options.area, fillOpacity: 0.1 };
  }

  // Line and area charts show circle markers at data points (matches Ember)
  if (config.visualizationType === 'line' || config.visualizationType === 'area') {
    options.series = { marker: { symbol: 'circle' } };
  }

  // Add data labels when any series has showDataLabels enabled
  const showDataLabels = config.chartSeries?.some((s) => s.showDataLabels);
  if (showDataLabels) {
    options.series = {
      ...options.series,
      dataLabels: {
        enabled: true,
        // For column/bar charts, render labels inside bars so they aren't clipped at the axis ceiling.
        // Line/area labels stay above points (default).
        ...(primaryType === 'column' || primaryType === 'bar'
          ? { inside: true, verticalAlign: 'top' as const }
          : {}),
        color: 'var(--text-default)',
        style: {
          fontSize: '12px',
          fontWeight: 'normal',
          textOutline: '1px var(--base-module)',
        },
        formatter: isPercentStacking
          ? function (this: Highcharts.Point) {
              // `percentage` is available on Point in stacked percent charts
              const percentage = (this as Highcharts.Point & { percentage?: number }).percentage;
              if (typeof percentage === 'number') {
                // Round to 2 decimal places like Ember's percentageString()
                return `${Math.round(percentage * 100) / 100}%`;
              }
              return '';
            }
          : function (this: Highcharts.Point) {
              // Format all data labels using formatValue for consistent rounding
              return formatValue(this.y ?? null, primaryUnit, renderContext);
            },
      },
    };
  }

  return options;
}

/**
 * Builds pivot series from view_by + segment_by data.
 * Data shape: [[category_dim, segment_dim, val1, val2, ...], ...]
 * Output: One series per (segment, metric) combination
 */
function buildPivotSeries(
  rows: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numMetrics: number,
  renderContext: RenderContext,
): { series: SeriesWithMetricIndex[]; categories: string[] } {
  if (numMetrics <= 0) {
    return { series: [], categories: [] };
  }

  const categoryMap = new Map<string, number>();
  const categories: string[] = [];

  // segmentRawValue -> metric -> category -> value
  const segmentMetricData = new Map<string, Map<number, Map<string, number | null>>>();
  const segmentDisplayNames = new Map<string, string>();

  for (const row of rows) {
    const categoryCell = row[0];
    const segmentCell = row[1];

    if (!isDimensionCell(categoryCell) || !isDimensionCell(segmentCell)) continue;

    const categoryKey = String(categoryCell.rawValue);
    const segmentKey = String(segmentCell.rawValue);

    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, categories.length);
      categories.push(formatDimensionDisplayValue(categoryCell));
    }

    if (!segmentDisplayNames.has(segmentKey)) {
      let displayName = segmentCell.displayValue;
      if (config.segmentByTimeInterval && typeof segmentCell.rawValue === 'number') {
        displayName = formatTimeDimensionLabel(
          segmentCell.rawValue,
          config.segmentByTimeInterval,
          renderContext,
        );
      }
      segmentDisplayNames.set(segmentKey, displayName);
    }

    let segmentData = segmentMetricData.get(segmentKey);
    if (!segmentData) {
      segmentData = new Map();
      segmentMetricData.set(segmentKey, segmentData);
    }

    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      let metricData = segmentData.get(metricIndex);
      if (!metricData) {
        metricData = new Map();
        segmentData.set(metricIndex, metricData);
      }
      const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
      const metricValue = extractNumericValue(row, 2 + metricIndex, fractionUnit);
      metricData.set(categoryKey, metricValue);
    }
  }

  const series: SeriesWithMetricIndex[] = [];
  const categoryKeys = Array.from(categoryMap.keys());

  for (const [segmentRawValue, metricDataMap] of segmentMetricData) {
    const segmentDisplayName = segmentDisplayNames.get(segmentRawValue) ?? segmentRawValue;
    const color = getSeriesColor(config.segmentBy, segmentRawValue);
    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      const dataMap = metricDataMap.get(metricIndex);
      const data = categoryKeys.map((key) => dataMap?.get(key) ?? null);
      const seriesType = getSeriesTypeForIndex(
        metricIndex,
        numMetrics,
        config.visualizationType,
        primaryType,
      );

      const metricName = getSeriesName(metricIndex, metricMetadata, config);
      const seriesName =
        numMetrics > 1 ? `${segmentDisplayName} - ${metricName}` : segmentDisplayName;

      series.push({
        name: seriesName,
        data,
        type: seriesType,
        metricIndex,
        ...(color && { color }),
      });
    }
  }

  return { series, categories };
}

function extractTimeSeries(
  rows: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numDimensions: number,
  numMetrics: number,
): SeriesWithMetricIndex[] {
  const effectiveDimensions = Math.max(numDimensions, 1);
  if (numMetrics <= 0) return [];

  const series: SeriesWithMetricIndex[] = [];

  for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
    const name = getSeriesName(metricIndex, metricMetadata, config);
    const seriesType = getSeriesTypeForIndex(
      metricIndex,
      numMetrics,
      config.visualizationType,
      primaryType,
    );

    const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
    const data: [number, number | null][] = [];
    for (const row of rows) {
      const ts = extractTimestampMs(row[0]);
      if (ts === null) continue;
      data.push([ts, extractNumericValue(row, effectiveDimensions + metricIndex, fractionUnit)]);
    }

    series.push({ name, data, type: seriesType, metricIndex });
  }

  return series;
}

function buildTimePivotSeries(
  rows: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numMetrics: number,
): SeriesWithMetricIndex[] {
  if (numMetrics <= 0) return [];

  const timestampIndex = new Map<number, number>();
  const timestamps: number[] = [];

  const segmentMetricData = new Map<string, Map<number, Map<number, number | null>>>();
  const segmentDisplayNames = new Map<string, string>();

  for (const row of rows) {
    const ts = extractTimestampMs(row[0]);
    const segment = extractSegmentIdentity(row[1]);
    if (ts === null || !segment) continue;

    if (!timestampIndex.has(ts)) {
      timestampIndex.set(ts, timestamps.length);
      timestamps.push(ts);
    }

    if (!segmentDisplayNames.has(segment.key)) {
      segmentDisplayNames.set(segment.key, segment.displayName);
    }

    let segmentData = segmentMetricData.get(segment.key);
    if (!segmentData) {
      segmentData = new Map();
      segmentMetricData.set(segment.key, segmentData);
    }

    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      let metricData = segmentData.get(metricIndex);
      if (!metricData) {
        metricData = new Map();
        segmentData.set(metricIndex, metricData);
      }
      const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
      metricData.set(ts, extractNumericValue(row, 2 + metricIndex, fractionUnit));
    }
  }

  const series: SeriesWithMetricIndex[] = [];

  for (const [segmentKey, metricDataMap] of segmentMetricData) {
    const segmentDisplayName = segmentDisplayNames.get(segmentKey) ?? segmentKey;
    const color = getSeriesColor(config.segmentBy, segmentKey);
    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      const dataMap = metricDataMap.get(metricIndex);
      const data = timestamps.map(
        (ts) => [ts, dataMap?.get(ts) ?? null] as [number, number | null],
      );

      const seriesType = getSeriesTypeForIndex(
        metricIndex,
        numMetrics,
        config.visualizationType,
        primaryType,
      );

      const metricName = getSeriesName(metricIndex, metricMetadata, config);
      const seriesName =
        numMetrics > 1 ? `${segmentDisplayName} - ${metricName}` : segmentDisplayName;

      series.push({
        name: seriesName,
        data,
        type: seriesType,
        metricIndex,
        ...(color && { color }),
      });
    }
  }

  return series;
}

/**
 * Builds comparison series with proper alignment for both standard and pivot data.
 */
function buildComparisonSeries(
  previous: ChartData,
  currentData: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numDimensions: number,
  numMetrics: number,
  isTimeSeries: boolean,
  renderContext: RenderContext,
): SeriesWithMetricIndex[] {
  if (!isGroupedData(previous.data)) return [];

  const isPivot = Boolean(config.segmentBy) && numDimensions >= 2;

  if (isPivot) {
    return buildPivotComparisonSeries(
      previous.data,
      currentData,
      metricMetadata,
      config,
      primaryType,
      numMetrics,
      isTimeSeries,
      renderContext,
    );
  }

  return buildStandardComparisonSeries(
    previous.data,
    currentData,
    metricMetadata,
    config,
    primaryType,
    numDimensions,
    numMetrics,
    isTimeSeries,
    renderContext,
  );
}

function buildStandardComparisonSeries(
  previousData: GroupedData,
  currentData: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numDimensions: number,
  numMetrics: number,
  isTimeSeries: boolean,
  renderContext: RenderContext,
): SeriesWithMetricIndex[] {
  const effectiveDimensions = Math.max(numDimensions, 1);
  // Use formatted period label from dateRange when available, otherwise fall back to renderContext.labels or default
  const previousPeriodLabel =
    formatPeriodLabel(
      renderContext.dateRange,
      'previous',
      renderContext.locale,
      renderContext.timezone,
    ) ??
    renderContext.labels?.previousPeriod ??
    '(Previous)';

  if (isTimeSeries) {
    // Extract current period timestamps for x-axis positioning
    const currentTimestamps: number[] = [];
    for (const row of currentData) {
      const ts = extractTimestampMs(row[0]);
      if (ts !== null) {
        currentTimestamps.push(ts);
      }
    }

    if (numMetrics <= 0) return [];

    // Previous period timestamps aligned by position with currentTimestamps.
    const previousTimestamps: (number | null)[] = currentTimestamps.map((_, index) =>
      index < previousData.length ? extractTimestampMs(previousData[index][0]) : null,
    );

    const series: SeriesWithMetricIndex[] = [];
    // For single-metric time comparison, use just the period label; for multi-metric, prefix with metric name
    const usePeriodLabelOnly = numMetrics === 1 && renderContext.dateRange !== undefined;

    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      const baseName = getSeriesName(metricIndex, metricMetadata, config);
      const seriesType = getSeriesTypeForIndex(
        metricIndex,
        numMetrics,
        config.visualizationType,
        primaryType,
      );

      // Align previous period by position (index), not by timestamp.
      // Previous period has different timestamps but same number of data points.
      const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
      const data = currentTimestamps.map((ts, index) => {
        const y =
          index < previousData.length
            ? extractNumericValue(
                previousData[index],
                effectiveDimensions + metricIndex,
                fractionUnit,
              )
            : null;
        return [ts, y] as [number, number | null];
      });

      const seriesName = usePeriodLabelOnly
        ? previousPeriodLabel
        : `${baseName} - ${previousPeriodLabel}`;

      series.push({
        name: seriesName,
        data,
        type: seriesType,
        color: COMPARISON_COLOR_PALETTE[metricIndex % COMPARISON_COLOR_PALETTE.length],
        metricIndex,
        previousTimestamps,
      });
    }

    return series;
  }

  const previousByKey = new Map<string, DataRow>();
  for (const row of previousData) {
    const key = getDimensionKey(row, 1);
    if (key) {
      previousByKey.set(key, row);
    }
  }

  const currentKeys: string[] = [];
  for (const row of currentData) {
    const key = getDimensionKey(row, 1);
    if (key) {
      currentKeys.push(key);
    }
  }

  if (numMetrics <= 0) return [];

  const series: SeriesWithMetricIndex[] = [];
  // For single-metric time comparison, use just the period label; for multi-metric, prefix with metric name
  const usePeriodLabelOnly = numMetrics === 1 && renderContext.dateRange !== undefined;

  for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
    const baseName = getSeriesName(metricIndex, metricMetadata, config);
    const seriesType = getSeriesTypeForIndex(
      metricIndex,
      numMetrics,
      config.visualizationType,
      primaryType,
    );

    const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
    const data = currentKeys.map((key) => {
      const previousRow = previousByKey.get(key);
      if (!previousRow) return null;
      return extractNumericValue(previousRow, numDimensions + metricIndex, fractionUnit);
    });

    const seriesName = usePeriodLabelOnly
      ? previousPeriodLabel
      : `${baseName} ${previousPeriodLabel}`;

    series.push({
      name: seriesName,
      data,
      type: seriesType,
      color: COMPARISON_COLOR_PALETTE[metricIndex % COMPARISON_COLOR_PALETTE.length],
      metricIndex,
    });
  }

  return series;
}

function buildPivotComparisonSeries(
  previousData: GroupedData,
  currentData: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numMetrics: number,
  isTimeSeries: boolean,
  renderContext: RenderContext,
): SeriesWithMetricIndex[] {
  if (numMetrics <= 0) return [];

  // Use formatted period label from dateRange when available, otherwise fall back to renderContext.labels or default
  const previousPeriodLabel =
    formatPeriodLabel(
      renderContext.dateRange,
      'previous',
      renderContext.locale,
      renderContext.timezone,
    ) ??
    renderContext.labels?.previousPeriod ??
    '(Previous)';

  if (isTimeSeries) {
    const timestampMap = new Map<number, number>();
    const timestamps: number[] = [];

    const currentSegmentKeys = new Set<string>();
    const segmentDisplayNames = new Map<string, string>();

    for (const row of currentData) {
      const ts = extractTimestampMs(row[0]);
      const segment = extractSegmentIdentity(row[1]);
      if (ts === null || !segment) continue;

      if (!timestampMap.has(ts)) {
        timestampMap.set(ts, timestamps.length);
        timestamps.push(ts);
      }

      currentSegmentKeys.add(segment.key);
      if (!segmentDisplayNames.has(segment.key)) {
        segmentDisplayNames.set(segment.key, segment.displayName);
      }
    }

    const previousByCompositeKey = new Map<string, DataRow>();
    for (const row of previousData) {
      const ts = extractTimestampMs(row[0]);
      const segment = extractSegmentIdentity(row[1]);
      if (ts === null || !segment) continue;
      previousByCompositeKey.set(`${ts}|${segment.key}`, row);
    }

    const series: SeriesWithMetricIndex[] = [];
    for (const segmentKey of currentSegmentKeys) {
      const segmentDisplayName = segmentDisplayNames.get(segmentKey) ?? segmentKey;

      for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
        const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
        const data = timestamps.map((ts) => {
          const previousRow = previousByCompositeKey.get(`${ts}|${segmentKey}`);
          const y = previousRow
            ? extractNumericValue(previousRow, 2 + metricIndex, fractionUnit)
            : null;
          return [ts, y] as [number, number | null];
        });

        const seriesType = getSeriesTypeForIndex(
          metricIndex,
          numMetrics,
          config.visualizationType,
          primaryType,
        );

        const metricName = getSeriesName(metricIndex, metricMetadata, config);
        const baseName =
          numMetrics > 1 ? `${segmentDisplayName} - ${metricName}` : segmentDisplayName;

        series.push({
          name: `${baseName} ${previousPeriodLabel}`,
          data,
          type: seriesType,
          color: COMPARISON_COLOR_PALETTE[metricIndex % COMPARISON_COLOR_PALETTE.length],
          metricIndex,
        });
      }
    }

    return series;
  }

  const categoryMap = new Map<string, number>();
  const categories: string[] = [];
  const currentSegmentKeys = new Set<string>();
  const segmentDisplayNames = new Map<string, string>();

  for (const row of currentData) {
    const categoryCell = row[0];
    const segmentCell = row[1];
    if (!isDimensionCell(categoryCell) || !isDimensionCell(segmentCell)) continue;

    const categoryKey = String(categoryCell.rawValue);
    const segmentKey = String(segmentCell.rawValue);

    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, categories.length);
      categories.push(categoryKey);
    }
    currentSegmentKeys.add(segmentKey);
    if (!segmentDisplayNames.has(segmentKey)) {
      segmentDisplayNames.set(segmentKey, segmentCell.displayValue);
    }
  }

  // "categoryRawValue|segmentRawValue" -> row
  const previousByCompositeKey = new Map<string, DataRow>();
  for (const row of previousData) {
    const categoryCell = row[0];
    const segmentCell = row[1];
    if (!isDimensionCell(categoryCell) || !isDimensionCell(segmentCell)) continue;

    const compositeKey = `${categoryCell.rawValue}|${segmentCell.rawValue}`;
    previousByCompositeKey.set(compositeKey, row);
  }

  const series: SeriesWithMetricIndex[] = [];
  const categoryKeys = Array.from(categoryMap.keys());

  for (const segmentKey of currentSegmentKeys) {
    const segmentDisplayName = segmentDisplayNames.get(segmentKey) ?? segmentKey;

    for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
      const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
      const data = categoryKeys.map((categoryKey) => {
        const compositeKey = `${categoryKey}|${segmentKey}`;
        const previousRow = previousByCompositeKey.get(compositeKey);
        if (!previousRow) return null;
        return extractNumericValue(previousRow, 2 + metricIndex, fractionUnit);
      });

      const seriesType = getSeriesTypeForIndex(
        metricIndex,
        numMetrics,
        config.visualizationType,
        primaryType,
      );

      const metricName = getSeriesName(metricIndex, metricMetadata, config);
      const baseName =
        numMetrics > 1 ? `${segmentDisplayName} - ${metricName}` : segmentDisplayName;

      series.push({
        name: `${baseName} ${previousPeriodLabel}`,
        data,
        type: seriesType,
        color: COMPARISON_COLOR_PALETTE[metricIndex % COMPARISON_COLOR_PALETTE.length],
        metricIndex,
      });
    }
  }

  return series;
}

function getDimensionKey(row: DataRow, numDimensions: number): string | null {
  const parts: string[] = [];

  for (let i = 0; i < numDimensions; i++) {
    const cell = row[i];
    if (isDimensionCell(cell)) {
      parts.push(String(cell.rawValue));
    } else if (typeof cell === 'string') {
      parts.push(cell);
    } else if (typeof cell === 'number') {
      parts.push(String(cell));
    } else {
      return null;
    }
  }

  return parts.join('|');
}

function extractCategories(rows: GroupedData): string[] {
  return rows.map((row) => {
    const firstCell = row[0];
    if (isDimensionCell(firstCell)) {
      return formatDimensionDisplayValue(firstCell);
    }
    if (typeof firstCell === 'string') {
      return firstCell;
    }
    if (typeof firstCell === 'number') {
      return String(firstCell);
    }
    return '';
  });
}

function extractSeries(
  rows: GroupedData,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
  primaryType: HighchartsSeriesType,
  numDimensions: number,
  numMetrics: number,
): SeriesWithMetricIndex[] {
  const effectiveDimensions = Math.max(numDimensions, 1);
  if (numMetrics <= 0) return [];

  const series: SeriesWithMetricIndex[] = [];

  for (let metricIndex = 0; metricIndex < numMetrics; metricIndex++) {
    const name = getSeriesName(metricIndex, metricMetadata, config);
    const seriesType = getSeriesTypeForIndex(
      metricIndex,
      numMetrics,
      config.visualizationType,
      primaryType,
    );
    const fractionUnit = metricMetadata[metricIndex]?.fractionUnit;
    const data = extractMetricData(rows, effectiveDimensions + metricIndex, fractionUnit);

    series.push({ name, data, type: seriesType, metricIndex });
  }

  return series;
}

function getSeriesName(
  metricIndex: number,
  metricMetadata: MetricMetadata[],
  config: ChartConfig,
): string {
  const chartSeries = config.chartSeries?.[metricIndex];
  const metadata = metricMetadata[metricIndex] as MetricMetadata | undefined;
  const fallbackName = metadata?.metricId ?? `Series ${metricIndex + 1}`;

  return getDisplayNameWithAggregation(chartSeries, fallbackName) ?? fallbackName;
}

function extractMetricData(
  rows: GroupedData,
  columnIndex: number,
  fractionUnit?: FractionUnit,
): (number | null)[] {
  return rows.map((row) => extractNumericValue(row, columnIndex, fractionUnit));
}

function extractNumericValue(
  row: DataRow,
  columnIndex: number,
  fractionUnit?: FractionUnit,
): number | null {
  const cell = row[columnIndex];

  if (cell === null) {
    return null;
  }

  if (typeof cell === 'number') {
    return cell;
  }

  if (isDualQueryCell(cell)) {
    return applyFractionUnit(cell, fractionUnit);
  }

  return null;
}

function extractTimestampMs(cell: DataRow[number]): number | null {
  if (isDimensionCell(cell)) {
    return typeof cell.rawValue === 'number' && Number.isFinite(cell.rawValue)
      ? cell.rawValue
      : null;
  }

  return typeof cell === 'number' && Number.isFinite(cell) ? cell : null;
}

function extractSegmentIdentity(
  cell: DataRow[number],
): { key: string; displayName: string } | null {
  if (isDimensionCell(cell)) {
    const key = String(cell.rawValue);
    return { key, displayName: cell.displayValue };
  }

  if (typeof cell === 'string') {
    return { key: cell, displayName: cell };
  }

  if (typeof cell === 'number') {
    const key = String(cell);
    return { key, displayName: key };
  }

  return null;
}

function emptyOptions(config: ChartConfig, renderContext: RenderContext): Highcharts.Options {
  const highchartsType = mapVisualizationType(config.visualizationType);
  const isTimeSeries = config.viewBy === 'time';
  return {
    chart: { type: highchartsType },
    colors: SERIES_COLOR_PALETTE,
    xAxis: isTimeSeries ? { type: 'datetime' } : { categories: [], type: 'category' },
    yAxis: { title: { text: undefined } },
    series: [],
    plotOptions: buildPlotOptions(config, highchartsType, [], renderContext),
    tooltip: { shared: true },
    legend: { enabled: false },
    credits: { enabled: false },
  };
}
